export function resolvePathToBox(d) {
  if (!d || d.trim() === '') {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  let currentX = 0;
  let currentY = 0;
  let startX = 0;
  let startY = 0;
  let prevControlX = 0;
  let prevControlY = 0;

  // Improved regex to handle path commands with proper grouping
  const commands = d.match(/[MLHVCSQTAZmlhvcsqtaz][^MLHVCSQTAZmlhvcsqtaz]*/g) || [];

  function updateBounds(x, y) {
    if (isNaN(x) || isNaN(y)) return;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  }

  // Enhanced cubic Bezier bounds calculation
  function cubicBezierBounds(x0, y0, x1, y1, x2, y2, x3, y3) {
    // Include endpoints
    updateBounds(x0, y0);
    updateBounds(x3, y3);

    // Calculate extrema for x and y separately
    function findCubicExtrema(p0, p1, p2, p3) {
      const a = 3 * (-p0 + 3 * p1 - 3 * p2 + p3);
      const b = 6 * (p0 - 2 * p1 + p2);
      const c = 3 * (p1 - p0);

      const extrema = [];

      if (Math.abs(a) > 1e-6) {
        const discriminant = b * b - 4 * a * c;
        if (discriminant >= 0) {
          const t1 = (-b + Math.sqrt(discriminant)) / (2 * a);
          const t2 = (-b - Math.sqrt(discriminant)) / (2 * a);
          [t1, t2].forEach((t) => {
            if (t > 0 && t < 1) {
              extrema.push(t);
            }
          });
        }
      } else if (Math.abs(b) > 1e-6) {
        const t = -c / b;
        if (t > 0 && t < 1) {
          extrema.push(t);
        }
      }

      return extrema;
    }

    // Find extrema for x coordinate
    const xExtrema = findCubicExtrema(x0, x1, x2, x3);
    xExtrema.forEach((t) => {
      const mt = 1 - t;
      const x = mt * mt * mt * x0 + 3 * mt * mt * t * x1 + 3 * mt * t * t * x2 + t * t * t * x3;
      const y = mt * mt * mt * y0 + 3 * mt * mt * t * y1 + 3 * mt * t * t * y2 + t * t * t * y3;
      updateBounds(x, y);
    });

    // Find extrema for y coordinate
    const yExtrema = findCubicExtrema(y0, y1, y2, y3);
    yExtrema.forEach((t) => {
      const mt = 1 - t;
      const x = mt * mt * mt * x0 + 3 * mt * mt * t * x1 + 3 * mt * t * t * x2 + t * t * t * x3;
      const y = mt * mt * mt * y0 + 3 * mt * mt * t * y1 + 3 * mt * t * t * y2 + t * t * t * y3;
      updateBounds(x, y);
    });
  }

  // Enhanced quadratic Bezier bounds calculation
  function quadraticBezierBounds(x0, y0, x1, y1, x2, y2) {
    updateBounds(x0, y0);
    updateBounds(x2, y2);

    // Find extrema for x coordinate
    const denomX = x0 - 2 * x1 + x2;
    if (Math.abs(denomX) > 1e-6) {
      const t = (x0 - x1) / denomX;
      if (t > 0 && t < 1) {
        const x = (1 - t) * (1 - t) * x0 + 2 * (1 - t) * t * x1 + t * t * x2;
        const y = (1 - t) * (1 - t) * y0 + 2 * (1 - t) * t * y1 + t * t * y2;
        updateBounds(x, y);
      }
    }

    // Find extrema for y coordinate
    const denomY = y0 - 2 * y1 + y2;
    if (Math.abs(denomY) > 1e-6) {
      const t = (y0 - y1) / denomY;
      if (t > 0 && t < 1) {
        const x = (1 - t) * (1 - t) * x0 + 2 * (1 - t) * t * x1 + t * t * x2;
        const y = (1 - t) * (1 - t) * y0 + 2 * (1 - t) * t * y1 + t * t * y2;
        updateBounds(x, y);
      }
    }
  }

  // Convert an SVG elliptical arc to one or more cubic Bezier curves (exact conversion)
  // Implementation based on https://www.w3.org/TR/SVG/implnote.html#ArcImplementationNotes
  function arcToCubicCurves(x1, y1, rx, ry, angle, largeArcFlag, sweepFlag, x2, y2) {
    if (rx === 0 || ry === 0) return [];

    const phi = (angle * Math.PI) / 180;
    const cosPhi = Math.cos(phi);
    const sinPhi = Math.sin(phi);

    // Step 1: Compute (x1', y1')
    const dx2 = (x1 - x2) / 2;
    const dy2 = (y1 - y2) / 2;
    const x1p = cosPhi * dx2 + sinPhi * dy2;
    const y1p = -sinPhi * dx2 + cosPhi * dy2;

    let rxs = Math.abs(rx);
    let rys = Math.abs(ry);

    // Correct radii
    const lambda = (x1p * x1p) / (rxs * rxs) + (y1p * y1p) / (rys * rys);
    if (lambda > 1) {
      const s = Math.sqrt(lambda);
      rxs *= s;
      rys *= s;
    }

    // Step 2: Compute (cx', cy')
    const sign = largeArcFlag === sweepFlag ? -1 : 1;
    const rxs2 = rxs * rxs;
    const rys2 = rys * rys;
    const x1p2 = x1p * x1p;
    const y1p2 = y1p * y1p;
    const num = Math.max(0, rxs2 * rys2 - rxs2 * y1p2 - rys2 * x1p2);
    const den = rxs2 * y1p2 + rys2 * x1p2;
    const coef = sign * Math.sqrt(num / den);
    const cxp = (coef * (rxs * y1p)) / rys;
    const cyp = (coef * -(rys * x1p)) / rxs;

    // Step 3: Compute (cx, cy)
    const cx = cosPhi * cxp - sinPhi * cyp + (x1 + x2) / 2;
    const cy = sinPhi * cxp + cosPhi * cyp + (y1 + y2) / 2;

    // Step 4: Compute start and sweep angles
    function angleBetween(uX, uY, vX, vY) {
      const dot = uX * vX + uY * vY;
      const mag = Math.sqrt(uX * uX + uY * uY) * Math.sqrt(vX * vX + vY * vY);
      let ang = Math.acos(Math.max(-1, Math.min(1, dot / mag)));
      if (uX * vY - uY * vX < 0) ang = -ang;
      return ang;
    }

    const ux = (x1p - cxp) / rxs;
    const uy = (y1p - cyp) / rys;
    const vx = (-x1p - cxp) / rxs;
    const vy = (-y1p - cyp) / rys;

    const theta1 = angleBetween(1, 0, ux, uy);
    let deltaTheta = angleBetween(ux, uy, vx, vy);
    if (!sweepFlag && deltaTheta > 0) deltaTheta -= 2 * Math.PI;
    if (sweepFlag && deltaTheta < 0) deltaTheta += 2 * Math.PI;

    // Split into segments of max 90 degrees
    const segments = Math.max(1, Math.ceil(Math.abs(deltaTheta) / (Math.PI / 2)));
    const delta = deltaTheta / segments;

    const curves = [];
    for (let i = 0; i < segments; i++) {
      const t1 = theta1 + i * delta;
      const t2 = t1 + delta;

      // Calculate endpoints
      const cosT1 = Math.cos(t1);
      const sinT1 = Math.sin(t1);
      const cosT2 = Math.cos(t2);
      const sinT2 = Math.sin(t2);

      const e1x = cx + rxs * (cosPhi * cosT1 - sinPhi * sinT1);
      const e1y = cy + rys * (sinPhi * cosT1 + cosPhi * sinT1);
      const e2x = cx + rxs * (cosPhi * cosT2 - sinPhi * sinT2);
      const e2y = cy + rys * (sinPhi * cosT2 + cosPhi * sinT2);

      // Derivatives for control points
      const alpha = (4 / 3) * Math.tan((t2 - t1) / 4);
      const q1x = e1x - alpha * (rxs * (cosPhi * -sinT1 + sinPhi * cosT1));
      const q1y = e1y - alpha * (rys * (sinPhi * -sinT1 - cosPhi * cosT1));
      const q2x = e2x + alpha * (rxs * (cosPhi * -sinT2 + sinPhi * cosT2));
      const q2y = e2y + alpha * (rys * (sinPhi * -sinT2 - cosPhi * cosT2));

      curves.push({
        x1: e1x,
        y1: e1y,
        cx1: q1x,
        cy1: q1y,
        cx2: q2x,
        cy2: q2y,
        x2: e2x,
        y2: e2y,
      });
    }

    return curves;
  }

  commands.forEach((command) => {
    const type = command[0];
    const coords = command.slice(1).trim();

    // For commands without coordinates, handle special cases
    if (!coords && type.toLowerCase() !== 'z') return;

    const isAbsolute = type === type.toUpperCase();
    const numbers = coords
      ? coords
          .match(/-?\d*\.?\d+(?:e[+-]?\d+)?/gi)
          .map(Number)
          .filter((n) => !isNaN(n))
      : [];

    switch (type.toLowerCase()) {
      case 'm': // Move to
        for (let i = 0; i < numbers.length; i += 2) {
          if (i + 1 < numbers.length) {
            const x = isAbsolute ? numbers[i] : currentX + numbers[i];
            const y = isAbsolute ? numbers[i + 1] : currentY + numbers[i + 1];

            if (i === 0) {
              startX = x;
              startY = y;
              prevControlX = x;
              prevControlY = y;
              updateBounds(x, y); // Always update bounds for first moveto
            } else {
              // Subsequent coordinates in moveto are treated as lineto
              updateBounds(x, y);
            }

            currentX = x;
            currentY = y;
          }
        }
        break;

      case 'l': // Line to
        for (let i = 0; i < numbers.length; i += 2) {
          if (i + 1 < numbers.length) {
            const x = isAbsolute ? numbers[i] : currentX + numbers[i];
            const y = isAbsolute ? numbers[i + 1] : currentY + numbers[i + 1];

            currentX = x;
            currentY = y;
            updateBounds(x, y);
            prevControlX = x;
            prevControlY = y;
          }
        }
        break;

      case 'h': // Horizontal line
        for (let i = 0; i < numbers.length; i++) {
          const x = isAbsolute ? numbers[i] : currentX + numbers[i];
          currentX = x;
          updateBounds(x, currentY);
          prevControlX = x;
          prevControlY = currentY;
        }
        break;

      case 'v': // Vertical line
        for (let i = 0; i < numbers.length; i++) {
          const y = isAbsolute ? numbers[i] : currentY + numbers[i];
          currentY = y;
          updateBounds(currentX, y);
          prevControlX = currentX;
          prevControlY = y;
        }
        break;

      case 'c': // Cubic Bezier curve
        for (let i = 0; i < numbers.length; i += 6) {
          if (i + 5 < numbers.length) {
            const x1 = isAbsolute ? numbers[i] : currentX + numbers[i];
            const y1 = isAbsolute ? numbers[i + 1] : currentY + numbers[i + 1];
            const x2 = isAbsolute ? numbers[i + 2] : currentX + numbers[i + 2];
            const y2 = isAbsolute ? numbers[i + 3] : currentY + numbers[i + 3];
            const x = isAbsolute ? numbers[i + 4] : currentX + numbers[i + 4];
            const y = isAbsolute ? numbers[i + 5] : currentY + numbers[i + 5];

            prevControlX = x2;
            prevControlY = y2;

            cubicBezierBounds(currentX, currentY, x1, y1, x2, y2, x, y);

            currentX = x;
            currentY = y;
          }
        }
        break;

      case 's': // Smooth cubic Bezier curve
        for (let i = 0; i < numbers.length; i += 4) {
          if (i + 3 < numbers.length) {
            const x1 = currentX + (currentX - prevControlX);
            const y1 = currentY + (currentY - prevControlY);
            const x2 = isAbsolute ? numbers[i] : currentX + numbers[i];
            const y2 = isAbsolute ? numbers[i + 1] : currentY + numbers[i + 1];
            const x = isAbsolute ? numbers[i + 2] : currentX + numbers[i + 2];
            const y = isAbsolute ? numbers[i + 3] : currentY + numbers[i + 3];

            prevControlX = x2;
            prevControlY = y2;

            cubicBezierBounds(currentX, currentY, x1, y1, x2, y2, x, y);

            currentX = x;
            currentY = y;
          }
        }
        break;

      case 'q': // Quadratic Bezier curve
        for (let i = 0; i < numbers.length; i += 4) {
          if (i + 3 < numbers.length) {
            const x1 = isAbsolute ? numbers[i] : currentX + numbers[i];
            const y1 = isAbsolute ? numbers[i + 1] : currentY + numbers[i + 1];
            const x = isAbsolute ? numbers[i + 2] : currentX + numbers[i + 2];
            const y = isAbsolute ? numbers[i + 3] : currentY + numbers[i + 3];

            prevControlX = x1;
            prevControlY = y1;

            quadraticBezierBounds(currentX, currentY, x1, y1, x, y);

            currentX = x;
            currentY = y;
          }
        }
        break;

      case 't': // Smooth quadratic Bezier curve
        for (let i = 0; i < numbers.length; i += 2) {
          if (i + 1 < numbers.length) {
            const x1 = currentX + (currentX - prevControlX);
            const y1 = currentY + (currentY - prevControlY);
            const x = isAbsolute ? numbers[i] : currentX + numbers[i];
            const y = isAbsolute ? numbers[i + 1] : currentY + numbers[i + 1];

            prevControlX = x1;
            prevControlY = y1;

            quadraticBezierBounds(currentX, currentY, x1, y1, x, y);

            currentX = x;
            currentY = y;
          }
        }
        break;

      case 'a': // Arc -> approximate with cubic Bezier curves for robust bounds
        for (let i = 0; i < numbers.length; i += 7) {
          if (i + 6 < numbers.length) {
            const rx = Math.abs(numbers[i]);
            const ry = Math.abs(numbers[i + 1]);
            const xAxisRotation = numbers[i + 2];
            const largeArcFlag = numbers[i + 3] ? 1 : 0;
            const sweepFlag = numbers[i + 4] ? 1 : 0;
            const x = isAbsolute ? numbers[i + 5] : currentX + numbers[i + 5];
            const y = isAbsolute ? numbers[i + 6] : currentY + numbers[i + 6];

            const curves = arcToCubicCurves(
              currentX,
              currentY,
              rx,
              ry,
              xAxisRotation,
              largeArcFlag,
              sweepFlag,
              x,
              y,
            );
            if (curves.length === 0) {
              // Fallback: include endpoints
              updateBounds(currentX, currentY);
              updateBounds(x, y);
            } else {
              // First segment starts from current point; update bounds for each cubic
              let startXc = currentX;
              let startYc = currentY;
              for (const seg of curves) {
                cubicBezierBounds(
                  startXc,
                  startYc,
                  seg.cx1,
                  seg.cy1,
                  seg.cx2,
                  seg.cy2,
                  seg.x2,
                  seg.y2,
                );
                startXc = seg.x2;
                startYc = seg.y2;
              }
            }

            currentX = x;
            currentY = y;
            prevControlX = x;
            prevControlY = y;
          }
        }
        break;

      case 'z': // Close path
        if (startX !== currentX || startY !== currentY) {
          updateBounds(startX, startY);
        }
        currentX = startX;
        currentY = startY;
        prevControlX = startX;
        prevControlY = startY;
        break;
    }
  });

  // Handle case where no valid coordinates were found
  if (minX === Infinity || minY === Infinity || maxX === -Infinity || maxY === -Infinity) {
    return { x: 0, y: 0, width: 0, height: 0 };
  }

  // Don't force minimum dimensions - use actual calculated bounds
  const width = maxX - minX;
  const height = maxY - minY;

  return {
    x: minX,
    y: minY,
    width: width,
    height: height,
  };
}

// Helper function to apply transform to path coordinates
export function applyTransformToPath(d, transform) {
  if (!transform || !d) return d;

  // Parse path commands and apply transform to coordinates
  return d.replace(/([MLHVCSQTAmlhvcsqta])\s*([^A-Za-z]+)?/g, (match, command, coords) => {
    if (!coords) return match;

    const isAbsolute = command === command.toUpperCase();
    // Use improved number parsing to handle negative numbers and scientific notation
    const numbers =
      coords
        .trim()
        .match(/-?\d*\.?\d+(?:e[+-]?\d+)?/gi)
        ?.map(Number)
        .filter((n) => !isNaN(n)) || [];

    if (numbers.length === 0) return command;

    if (isAbsolute) {
      // Apply transform to absolute coordinates based on command type
      switch (command) {
        case 'M':
        case 'L':
        case 'T':
          // Move/Line/SmoothQuadratic commands: x,y pairs
          for (let i = 0; i < numbers.length; i += 2) {
            if (i + 1 < numbers.length) {
              const x = numbers[i];
              const y = numbers[i + 1];
              // Apply transform matrix: [a, b, c, d, e, f] * [x, y, 1]
              numbers[i] = transform[0] * x + transform[2] * y + transform[4];
              numbers[i + 1] = transform[1] * x + transform[3] * y + transform[5];
            }
          }
          break;

        case 'H':
          // Horizontal line: only x coordinates
          for (let i = 0; i < numbers.length; i++) {
            const x = numbers[i];
            const y = 0; // Assume y=0 for transformation
            numbers[i] = transform[0] * x + transform[2] * y + transform[4];
          }
          break;

        case 'V':
          // Vertical line: only y coordinates
          for (let i = 0; i < numbers.length; i++) {
            const x = 0; // Assume x=0 for transformation
            const y = numbers[i];
            numbers[i] = transform[1] * x + transform[3] * y + transform[5];
          }
          break;

        case 'C':
          // Cubic Bezier: x1,y1,x2,y2,x,y groups
          for (let i = 0; i < numbers.length; i += 6) {
            for (let j = 0; j < 6 && i + j + 1 < numbers.length; j += 2) {
              const x = numbers[i + j];
              const y = numbers[i + j + 1];
              numbers[i + j] = transform[0] * x + transform[2] * y + transform[4];
              numbers[i + j + 1] = transform[1] * x + transform[3] * y + transform[5];
            }
          }
          break;

        case 'S':
          // Smooth cubic Bezier: x2,y2,x,y groups
          for (let i = 0; i < numbers.length; i += 4) {
            for (let j = 0; j < 4 && i + j + 1 < numbers.length; j += 2) {
              const x = numbers[i + j];
              const y = numbers[i + j + 1];
              numbers[i + j] = transform[0] * x + transform[2] * y + transform[4];
              numbers[i + j + 1] = transform[1] * x + transform[3] * y + transform[5];
            }
          }
          break;

        case 'Q':
          // Quadratic Bezier: x1,y1,x,y groups
          for (let i = 0; i < numbers.length; i += 4) {
            for (let j = 0; j < 4 && i + j + 1 < numbers.length; j += 2) {
              const x = numbers[i + j];
              const y = numbers[i + j + 1];
              numbers[i + j] = transform[0] * x + transform[2] * y + transform[4];
              numbers[i + j + 1] = transform[1] * x + transform[3] * y + transform[5];
            }
          }
          break;

        case 'A':
          // Arc: rx,ry,x-axis-rotation,large-arc-flag,sweep-flag,x,y groups
          for (let i = 0; i < numbers.length; i += 7) {
            if (i + 6 < numbers.length) {
              // Transform only the end point (x,y), not the radii or flags
              const x = numbers[i + 5];
              const y = numbers[i + 6];
              numbers[i + 5] = transform[0] * x + transform[2] * y + transform[4];
              numbers[i + 6] = transform[1] * x + transform[3] * y + transform[5];

              // Transform radii if there's scaling
              const scaleX = Math.sqrt(transform[0] * transform[0] + transform[1] * transform[1]);
              const scaleY = Math.sqrt(transform[2] * transform[2] + transform[3] * transform[3]);
              numbers[i] *= scaleX; // rx
              numbers[i + 1] *= scaleY; // ry

              // Transform rotation angle if there's rotation in the matrix
              if (transform[1] !== 0 || transform[2] !== 0) {
                const rotation = (Math.atan2(transform[1], transform[0]) * 180) / Math.PI;
                numbers[i + 2] += rotation; // x-axis-rotation
              }
            }
          }
          break;

        default:
          // Default case: treat as x,y pairs
          for (let i = 0; i < numbers.length; i += 2) {
            if (i + 1 < numbers.length) {
              const x = numbers[i];
              const y = numbers[i + 1];
              numbers[i] = transform[0] * x + transform[2] * y + transform[4];
              numbers[i + 1] = transform[1] * x + transform[3] * y + transform[5];
            }
          }
          break;
      }
    } else {
      // For relative commands, only apply scaling and rotation (not translation)
      const relativeTransform = [transform[0], transform[1], transform[2], transform[3], 0, 0];

      switch (command.toLowerCase()) {
        case 'm':
        case 'l':
        case 't':
          // Move/Line/SmoothQuadratic commands: x,y pairs
          for (let i = 0; i < numbers.length; i += 2) {
            if (i + 1 < numbers.length) {
              const x = numbers[i];
              const y = numbers[i + 1];
              numbers[i] = relativeTransform[0] * x + relativeTransform[2] * y;
              numbers[i + 1] = relativeTransform[1] * x + relativeTransform[3] * y;
            }
          }
          break;

        case 'h':
          // Horizontal line: only x coordinates
          for (let i = 0; i < numbers.length; i++) {
            const x = numbers[i];
            numbers[i] = relativeTransform[0] * x;
          }
          break;

        case 'v':
          // Vertical line: only y coordinates
          for (let i = 0; i < numbers.length; i++) {
            const y = numbers[i];
            numbers[i] = relativeTransform[3] * y;
          }
          break;

        case 'c':
          // Cubic Bezier: x1,y1,x2,y2,x,y groups
          for (let i = 0; i < numbers.length; i += 6) {
            for (let j = 0; j < 6 && i + j + 1 < numbers.length; j += 2) {
              const x = numbers[i + j];
              const y = numbers[i + j + 1];
              numbers[i + j] = relativeTransform[0] * x + relativeTransform[2] * y;
              numbers[i + j + 1] = relativeTransform[1] * x + relativeTransform[3] * y;
            }
          }
          break;

        case 's':
          // Smooth cubic Bezier: x2,y2,x,y groups
          for (let i = 0; i < numbers.length; i += 4) {
            for (let j = 0; j < 4 && i + j + 1 < numbers.length; j += 2) {
              const x = numbers[i + j];
              const y = numbers[i + j + 1];
              numbers[i + j] = relativeTransform[0] * x + relativeTransform[2] * y;
              numbers[i + j + 1] = relativeTransform[1] * x + relativeTransform[3] * y;
            }
          }
          break;

        case 'q':
          // Quadratic Bezier: x1,y1,x,y groups
          for (let i = 0; i < numbers.length; i += 4) {
            for (let j = 0; j < 4 && i + j + 1 < numbers.length; j += 2) {
              const x = numbers[i + j];
              const y = numbers[i + j + 1];
              numbers[i + j] = relativeTransform[0] * x + relativeTransform[2] * y;
              numbers[i + j + 1] = relativeTransform[1] * x + relativeTransform[3] * y;
            }
          }
          break;

        case 'a':
          // Arc: rx,ry,x-axis-rotation,large-arc-flag,sweep-flag,x,y groups
          for (let i = 0; i < numbers.length; i += 7) {
            if (i + 6 < numbers.length) {
              // Transform only the end point delta (x,y)
              const x = numbers[i + 5];
              const y = numbers[i + 6];
              numbers[i + 5] = relativeTransform[0] * x + relativeTransform[2] * y;
              numbers[i + 6] = relativeTransform[1] * x + relativeTransform[3] * y;

              // Transform radii
              const scaleX = Math.sqrt(
                relativeTransform[0] * relativeTransform[0] +
                  relativeTransform[1] * relativeTransform[1],
              );
              const scaleY = Math.sqrt(
                relativeTransform[2] * relativeTransform[2] +
                  relativeTransform[3] * relativeTransform[3],
              );
              numbers[i] *= scaleX; // rx
              numbers[i + 1] *= scaleY; // ry

              // Transform rotation angle if there's rotation
              if (relativeTransform[1] !== 0 || relativeTransform[2] !== 0) {
                const rotation =
                  (Math.atan2(relativeTransform[1], relativeTransform[0]) * 180) / Math.PI;
                numbers[i + 2] += rotation; // x-axis-rotation
              }
            }
          }
          break;

        default:
          // Default case: treat as x,y pairs
          for (let i = 0; i < numbers.length; i += 2) {
            if (i + 1 < numbers.length) {
              const x = numbers[i];
              const y = numbers[i + 1];
              numbers[i] = relativeTransform[0] * x + relativeTransform[2] * y;
              numbers[i + 1] = relativeTransform[1] * x + relativeTransform[3] * y;
            }
          }
          break;
      }
    }

    return command + ' ' + numbers.join(' ');
  });
}

export function resolveNormalizedPath(d, pathBounds) {
  // Normalize path data to use pathBounds.x,y as the origin (0,0)
  let normalizedPath = d;
  if (pathBounds.x !== 0 || pathBounds.y !== 0) {
    try {
      // Process path commands with their coordinates
      normalizedPath = d.replace(
        /([MLHVCSQTAmlhvcsqta])\s*([^A-Za-z]*)/g,
        (match, command, coords) => {
          if (!coords || coords.trim() === '') return command; // Command with no coordinates

          const isAbsolute = command === command.toUpperCase();
          // Improved number parsing to handle negative numbers and scientific notation
          const numbers =
            coords
              .trim()
              .match(/-?\d*\.?\d+(?:e[+-]?\d+)?/gi)
              ?.map(Number)
              .filter((n) => !isNaN(n)) || [];

          if (numbers.length === 0) return command;

          // Only adjust absolute commands
          if (isAbsolute) {
            // Handle different command types
            switch (command) {
              case 'H': // Horizontal line - only x coordinate
                for (let i = 0; i < numbers.length; i++) {
                  numbers[i] -= pathBounds.x;
                }
                break;
              case 'V': // Vertical line - only y coordinate
                for (let i = 0; i < numbers.length; i++) {
                  numbers[i] -= pathBounds.y;
                }
                break;
              case 'A': // Arc - x,y are at positions 5,6 in each 7-tuple
                for (let i = 0; i < numbers.length; i += 7) {
                  if (i + 6 < numbers.length) {
                    numbers[i + 5] -= pathBounds.x;
                    numbers[i + 6] -= pathBounds.y;
                  }
                }
                break;
              default: // M, L, C, S, Q, T - x,y pairs
                for (let i = 0; i < numbers.length; i += 2) {
                  if (i + 1 < numbers.length) {
                    numbers[i] -= pathBounds.x;
                    numbers[i + 1] -= pathBounds.y;
                  }
                }
            }
          }

          return command + ' ' + numbers.join(' ');
        },
      );
    } catch (e) {
      console.warn('Could not normalize path:', e);
    }
  }
  return normalizedPath;
}

export function scalePathNode(childBox, newWidth, newHeight, maintainAspectRatio = true) {
  const result = {
    path: scalePathByDimensions(
      childBox.path,
      childBox.w,
      childBox.h,
      newWidth,
      newHeight,
      maintainAspectRatio,
    ),
  };

  // Scale stroke width proportionally if it exists
  if (childBox.strokeWidth) {
    const scaleX = newWidth / childBox.w;
    const scaleY = newHeight / childBox.h;
    const scale = maintainAspectRatio ? Math.min(scaleX, scaleY) : Math.sqrt(scaleX * scaleY);
    result.strokeWidth = childBox.strokeWidth * scale;
  }

  return result;
}

/**
 * Scale a path string based on width and height changes
 * @param {string} pathData - The SVG path data string
 * @param {number} originalWidth - Original width of the path bounds
 * @param {number} originalHeight - Original height of the path bounds
 * @param {number} newWidth - New width to scale to
 * @param {number} newHeight - New height to scale to
 * @param {boolean} maintainAspectRatio - Whether to maintain aspect ratio (default: true)
 * @returns {string} - The scaled path data string
 */
export function scalePathByDimensions(
  pathData,
  originalWidth,
  originalHeight,
  newWidth,
  newHeight,
  maintainAspectRatio = true,
) {
  if (!pathData || originalWidth <= 0 || originalHeight <= 0) return pathData;

  let scaleX = newWidth / originalWidth;
  let scaleY = newHeight / originalHeight;

  // Maintain aspect ratio by using the smaller scale factor
  if (maintainAspectRatio) {
    const scale = Math.min(scaleX, scaleY);
    scaleX = scale;
    scaleY = scale;
  }

  // If no scaling needed, return original path
  if (scaleX === 1 && scaleY === 1) return pathData;

  // Apply scaling transformation to path commands
  return pathData.replace(/([MLHVCSQTAmlhvcsqta])\s*([^A-Za-z]+)?/g, (match, command, coords) => {
    if (!coords) return match;

    // Use improved number parsing to handle negative numbers and scientific notation
    const numbers =
      coords
        .trim()
        .match(/-?\d*\.?\d+(?:e[+-]?\d+)?/gi)
        ?.map(Number)
        .filter((n) => !isNaN(n)) || [];

    if (numbers.length === 0) return command;

    // Apply scaling based on command type
    switch (command.toUpperCase()) {
      case 'M':
      case 'L':
      case 'T':
        // Move/Line/SmoothQuadratic commands: x,y pairs
        for (let i = 0; i < numbers.length; i += 2) {
          if (i + 1 < numbers.length) {
            numbers[i] *= scaleX; // x coordinate
            numbers[i + 1] *= scaleY; // y coordinate
          }
        }
        break;

      case 'H':
        // Horizontal line: only x coordinates
        for (let i = 0; i < numbers.length; i++) {
          numbers[i] *= scaleX;
        }
        break;

      case 'V':
        // Vertical line: only y coordinates
        for (let i = 0; i < numbers.length; i++) {
          numbers[i] *= scaleY;
        }
        break;

      case 'C':
        // Cubic Bezier: x1,y1,x2,y2,x,y groups
        for (let i = 0; i < numbers.length; i += 6) {
          for (let j = 0; j < 6 && i + j + 1 < numbers.length; j += 2) {
            numbers[i + j] *= scaleX; // x coordinate
            numbers[i + j + 1] *= scaleY; // y coordinate
          }
        }
        break;

      case 'S':
        // Smooth cubic Bezier: x2,y2,x,y groups
        for (let i = 0; i < numbers.length; i += 4) {
          for (let j = 0; j < 4 && i + j + 1 < numbers.length; j += 2) {
            numbers[i + j] *= scaleX; // x coordinate
            numbers[i + j + 1] *= scaleY; // y coordinate
          }
        }
        break;

      case 'Q':
        // Quadratic Bezier: x1,y1,x,y groups
        for (let i = 0; i < numbers.length; i += 4) {
          for (let j = 0; j < 4 && i + j + 1 < numbers.length; j += 2) {
            numbers[i + j] *= scaleX; // x coordinate
            numbers[i + j + 1] *= scaleY; // y coordinate
          }
        }
        break;

      case 'A':
        // Arc: rx,ry,x-axis-rotation,large-arc-flag,sweep-flag,x,y groups
        for (let i = 0; i < numbers.length; i += 7) {
          if (i + 6 < numbers.length) {
            numbers[i] *= scaleX; // rx (radius x)
            numbers[i + 1] *= scaleY; // ry (radius y)
            // i + 2: x-axis-rotation (angle - no scaling needed)
            // i + 3: large-arc-flag (0 or 1 - no scaling needed)
            // i + 4: sweep-flag (0 or 1 - no scaling needed)
            numbers[i + 5] *= scaleX; // x coordinate
            numbers[i + 6] *= scaleY; // y coordinate
          }
        }
        break;

      default:
        // Default case: treat as x,y pairs
        for (let i = 0; i < numbers.length; i += 2) {
          if (i + 1 < numbers.length) {
            numbers[i] *= scaleX; // x coordinate
            numbers[i + 1] *= scaleY; // y coordinate
          }
        }
        break;
    }

    return command + ' ' + numbers.join(' ');
  });
}

/**
 * Scale a path string uniformly by a single scale factor
 * @param {string} pathData - The SVG path data string
 * @param {number} scale - The scale factor to apply
 * @returns {string} - The scaled path data string
 */
export function scalePathUniformly(pathData, scale) {
  if (!pathData || scale === 1) return pathData;
  return scalePathByDimensions(pathData, 1, 1, scale, scale, false);
}
